import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
  Divider,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@material-ui/icons';
import DateField from '../../components/Controllers/DateField';
import { 
  Cms1500Form, 
  Cms1500Service, 
  createEmptyCMS1500Service,
  getMedicareRates 
} from '../../services/cms1500Service';
import { useClaims } from '../../contexts/ClaimsContext';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1),
  },
  fieldLabel: {
    fontWeight: 'bold',
    fontSize: '0.8rem',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 'bold',
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  medicareRateGreen: {
    borderColor: '#4caf50',
    color: '#4caf50',
  },
  medicareRateOrange: {
    borderColor: '#ff9800',
    color: '#ff9800',
  },
  medicareRateRed: {
    borderColor: '#f44336',
    color: '#f44336',
  },
  refreshButton: {
    marginLeft: theme.spacing(1),
  },
  medicareRatesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  serviceCell: {
    '& td': {
      padding: '4px !important',
    }
  }
}));

interface CMS1500FormProps {
  formData: Cms1500Form;
  onFormChange: (formData: Cms1500Form) => void;
  onSave: () => void;
  onAddForm?: () => void;
}

const CMS1500FormComponent: React.FC<CMS1500FormProps> = ({ formData, onFormChange, onSave, onAddForm }) => {
  const classes = useStyles();
  const [form, setForm] = useState<Cms1500Form>(formData);
  const [loadingMedicareRates, setLoadingMedicareRates] = useState<boolean>(false);
  const { selectedClaimId } = useClaims();
  // const [claimId, setClaimId] = useState<string>(''); // This would come from the parent component in a real implementation

  useEffect(() => {
    setForm(formData);
  }, [formData]);
  
  // Fetch Medicare rates from the API
  const fetchMedicareRates = async () => {
    if (!form.id || !form.providerZip) {
      console.warn('Cannot fetch Medicare rates: missing form ID or provider ZIP');
      return;
    }

    if (!selectedClaimId) {
      console.warn('No selected claim ID available for fetching Medicare rates');
      return;
    }
    
    try {
      setLoadingMedicareRates(true);
      const servicesWithRates = await getMedicareRates(selectedClaimId, form.id);
      
      // Update the form with the Medicare rates
      const updatedServices = form.services.map((service, index) => {
        const serviceWithRate = servicesWithRates.find(s => s.id === service.id);
        if (serviceWithRate) {
          return {
            ...service,
            medicareRate: parseFloat((serviceWithRate.medicareRate || 0).toFixed(2)),
            multiplier: serviceWithRate.multiplier
          };
        }
        return service;
      });
      
      const updatedForm = {
        ...form,
        services: updatedServices
      };
      
      setForm(updatedForm);
      // onFormChange(updatedForm);
    } catch (error) {
      console.error('Error fetching Medicare rates:', error);
    } finally {
      setLoadingMedicareRates(false);
    }
  };
  
  // Get the CSS class for the multiplier based on its value
  const getMultiplierClass = (multiplier: number | null | undefined): string => {
    if (multiplier === null || multiplier === undefined) return '';
    
    if (multiplier < 2.5) {
      return classes.medicareRateGreen;
    } else if (multiplier <= 5) {
      return classes.medicareRateOrange;
    } else {
      return classes.medicareRateRed;
    }
  };

  const handleInputChange = (field: keyof Cms1500Form, value: any) => {
    const updatedForm = {
      ...form,
      [field]: value,
      isDirty: true,
    };
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  const handleServiceChange = (index: number, field: keyof Cms1500Service, value: any) => {
    const updatedServices = [...form.services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value,
      isDirty: true,
    };

    const updatedForm = {
      ...form,
      services: updatedServices,
      isDirty: true,
    };
    
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  const handleAddService = () => {
    const updatedForm = {
      ...form,
      services: [...form.services, createEmptyCMS1500Service()],
      isDirty: true,
    };
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  const handleDeleteService = (index: number) => {
    const updatedServices = [...form.services];
    updatedServices.splice(index, 1);

    const updatedForm = {
      ...form,
      services: updatedServices,
      isDirty: true,
    };
    
    setForm(updatedForm);
    onFormChange(updatedForm);
  };

  // Calculate total charges
  const calculateTotalCharges = () => {
    return form.services.reduce((total, service) => total + (service.totalCharge || 0), 0);
  };

  // Update total sum charge when services change
  useEffect(() => {
    const totalCharges = calculateTotalCharges();
    if (totalCharges !== form.totalSumCharge) {
      handleInputChange('totalSumCharge', totalCharges);
    }
  }, [form.services]);

  return (
    <Paper className={classes.root}>
      <Box className={classes.formSection}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Provider Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Provider Name"
              fullWidth
              value={form.providerName}
              onChange={(e) => handleInputChange('providerName', e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Provider Address"
              fullWidth
              value={form.providerAddress}
              onChange={(e) => handleInputChange('providerAddress', e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="City"
              fullWidth
              value={form.providerCity}
              onChange={(e) => handleInputChange('providerCity', e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="State"
              fullWidth
              value={form.providerState}
              onChange={(e) => handleInputChange('providerState', e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="ZIP"
              fullWidth
              value={form.providerZip}
              onChange={(e) => handleInputChange('providerZip', e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box className={classes.formSection}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Patient Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Patient Name"
              fullWidth
              value={form.patientName}
              onChange={(e) => handleInputChange('patientName', e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Policy Number"
              fullWidth
              value={form.policyNumber}
              onChange={(e) => handleInputChange('policyNumber', e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box className={classes.formSection}>
        <div className={classes.medicareRatesHeader}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Services
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={loadingMedicareRates ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={fetchMedicareRates}
            disabled={loadingMedicareRates || !form.id || !form.providerZip}
            className={classes.refreshButton}
          >
            Calculate Medicare Rates
          </Button>
        </div>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Service From</TableCell>
                <TableCell className={classes.tableHeader}>Service To</TableCell>
                <TableCell className={classes.tableHeader}>CPT/HCPCS Code</TableCell>
                <TableCell className={classes.tableHeader}>Modifier</TableCell>
                <TableCell className={classes.tableHeader}>Diagnosis Pointer</TableCell>
                <TableCell className={classes.tableHeader}>Service Units</TableCell>
                <TableCell className={classes.tableHeader}>Charge</TableCell>
                <TableCell className={classes.tableHeader}>Medicare Rate</TableCell>
                <TableCell className={classes.tableHeader}>Multiplier</TableCell>
                <TableCell className={classes.tableHeader}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.services.map((service, index) => (
                <TableRow key={index} className={classes.serviceCell}>
                  <TableCell>
                    <DateField
                      fullWidth
                      value={service.serviceFromDate}
                      onChange={(date) => handleServiceChange(index, 'serviceFromDate', date)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <DateField
                      fullWidth
                      value={service.serviceToDate}
                      onChange={(date) => handleServiceChange(index, 'serviceToDate', date)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      value={service.cpt}
                      onChange={(e) => handleServiceChange(index, 'cpt', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      value={service.modifier}
                      onChange={(e) => handleServiceChange(index, 'modifier', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      value={service.diagnosisPointer}
                      onChange={(e) => handleServiceChange(index, 'diagnosisPointer', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="number"
                      value={service.serviceUnits}
                      onChange={(e) => handleServiceChange(index, 'serviceUnits', Number(e.target.value))}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="number"
                      value={service.totalCharge}
                      onChange={(e) => handleServiceChange(index, 'totalCharge', Number(e.target.value))}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="number"
                      value={service.medicareRate || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      type="number"
                      value={service.multiplier || ''}
                      InputProps={{
                        readOnly: true,
                        className: getMultiplierClass(service.multiplier),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      className={classes.deleteButton}
                      onClick={() => handleDeleteService(index)}
                      disabled={form.services.length <= 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddService}
          className={classes.addButton}
        >
          Add Service
        </Button>
      </Box>

      <Divider />

      <Box className={classes.formSection}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Total Charges"
              fullWidth
              type="number"
              value={form.totalSumCharge}
              InputProps={{
                readOnly: true,
              }}
              margin="normal"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.formActions}>
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={!form.isDirty}
        >
          Save Form
        </Button>
        {onAddForm && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onAddForm}
            style={{ marginLeft: 8 }}
          >
            Add New Form
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default CMS1500FormComponent;


// Helper function to format date to MM/DD/YYYY
const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

// Helper function to parse date string to Date object
const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  // Check if the string matches MM/DD/YYYY format
  const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = dateString.match(regex);
  
  if (!match) return null;
  
  const month = parseInt(match[1], 10) - 1; // Months are 0-indexed in JS
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  
  const date = new Date(year, month, day);
  
  // Validate the date is valid
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  
  return date;
};